import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, Upload, Trash2, Loader2, Calendar, User, Building, Stethoscope, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import RegisterPopup from '../components/RegisterPopup';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const MedicalRecords = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Fetch medical records from database
  const fetchRecords = async () => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/medical-records/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setRecords(data);
      }
    } catch (error) {
      console.error("Error fetching medical records:", error);
      toast.error("Không thể tải hồ sơ y tế");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setShowRegisterPopup(true);
      setLoading(false);
    } else {
      fetchRecords();
    }
  }, [isLoggedIn]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      toast.error("Vui lòng đăng nhập để tải hồ sơ");
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Vui lòng chọn file hình ảnh");
      return;
    }

    setUploading(true);

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64String = event.target.result.split(',')[1];

        try {
          const res = await fetch(`${API_BASE}/medical-records`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: parseInt(userId),
              image_base64: base64String
            })
          });

          if (res.ok) {
            const data = await res.json();
            setRecords([data.record, ...records]);
            toast.success('Đã tải lên và phân tích hồ sơ y tế!');
          } else {
            const error = await res.json();
            toast.error(error.detail || 'Không thể tải hồ sơ');
          }
        } catch (error) {
          console.error("Error uploading medical record:", error);
          toast.error('Lỗi kết nối máy chủ');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error('Lỗi đọc file');
      setUploading(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      const res = await fetch(`${API_BASE}/medical-records/${recordId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        setRecords(records.filter(r => r.id !== recordId));
        toast.success('Đã xóa hồ sơ y tế');
      } else {
        toast.error('Không thể xóa hồ sơ');
      }
    } catch (error) {
      console.error("Error deleting medical record:", error);
      toast.error('Lỗi kết nối máy chủ');
    }
  };

  const viewRecordDetails = (record) => {
    setSelectedRecord(record);
    setIsDialogOpen(true);
  };

  const getRecordTypeBadge = (type) => {
    const colors = {
      'Siêu âm': 'bg-blue-100 text-blue-700',
      'Xét nghiệm': 'bg-green-100 text-green-700',
      'Khám thai': 'bg-pink-100 text-pink-700',
      'Đơn thuốc': 'bg-purple-100 text-purple-700',
      'Khác': 'bg-gray-100 text-gray-700'
    };
    return colors[type] || colors['Khác'];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              Hồ sơ y tế
            </span>
          </h1>
          <p className="text-2xl text-gray-600">
            Lưu trữ và quản lý hồ sơ khám bệnh của bạn
          </p>
        </div>

        {/* Upload Section */}
        <Card className="mb-8 border-pink-100 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mb-4">
                <Upload className="w-10 h-10 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Tải lên hồ sơ y tế</h3>
              <p className="text-gray-600 mb-4 text-center">
                Tải ảnh chụp hồ sơ y tế, AI sẽ tự động trích xuất thông tin
              </p>
              
              {uploading ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
                  <span className="text-gray-600">Đang phân tích hình ảnh...</span>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    id="medical-record-upload"
                  />
                  <label htmlFor="medical-record-upload" style={{ cursor: 'pointer' }}>
                    <Button 
                      type="button"
                      className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white"
                      onClick={() => document.getElementById('medical-record-upload').click()}
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Chọn hình ảnh
                    </Button>
                  </label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
            <span className="ml-2 text-gray-600">Đang tải hồ sơ...</span>
          </div>
        )}

        {/* Records List */}
        {!loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {records.length > 0 ? (
              records.map((record) => (
                <Card key={record.id} className="border-pink-100 shadow-md hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg text-gray-800 line-clamp-1">
                        {record.title || "Hồ sơ y tế"}
                      </CardTitle>
                      {record.record_type && (
                        <Badge className={getRecordTypeBadge(record.record_type)}>
                          {record.record_type}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {record.date && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-pink-500" />
                        <span className="text-sm">{record.date}</span>
                      </div>
                    )}
                    
                    {record.doctor && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <User className="w-4 h-4 text-pink-500" />
                        <span className="text-sm">{record.doctor}</span>
                      </div>
                    )}
                    
                    {record.hospital && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Building className="w-4 h-4 text-pink-500" />
                        <span className="text-sm line-clamp-1">{record.hospital}</span>
                      </div>
                    )}

                    {record.summary && (
                      <p className="text-gray-700 text-sm line-clamp-2 mt-2">
                        {record.summary}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{record.created_at}</span>
                      </div>
                    </div>

                    <div className="flex space-x-2 pt-3">
                      <Button
                        variant="outline"
                        className="flex-1 border-pink-300 text-pink-600 hover:bg-pink-50"
                        onClick={() => viewRecordDetails(record)}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Xem chi tiết
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteRecord(record.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-pink-100 p-12 md:col-span-2 lg:col-span-3">
                <div className="text-center text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-pink-300" />
                  <p className="text-lg mb-4">Chưa có hồ sơ y tế nào</p>
                  <p className="text-sm">Tải lên hình ảnh hồ sơ y tế để bắt đầu</p>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Record Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">
              {selectedRecord?.title || "Chi tiết hồ sơ y tế"}
            </DialogTitle>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 mt-4">
              {selectedRecord.record_type && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 font-medium">Loại:</span>
                  <Badge className={getRecordTypeBadge(selectedRecord.record_type)}>
                    {selectedRecord.record_type}
                  </Badge>
                </div>
              )}
              
              {selectedRecord.date && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <Calendar className="w-5 h-5 text-pink-500" />
                  <span className="font-medium">Ngày khám:</span>
                  <span>{selectedRecord.date}</span>
                </div>
              )}

              {selectedRecord.doctor && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <Stethoscope className="w-5 h-5 text-pink-500" />
                  <span className="font-medium">Bác sĩ:</span>
                  <span>{selectedRecord.doctor}</span>
                </div>
              )}

              {selectedRecord.hospital && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <Building className="w-5 h-5 text-pink-500" />
                  <span className="font-medium">Bệnh viện:</span>
                  <span>{selectedRecord.hospital}</span>
                </div>
              )}

              {selectedRecord.summary && (
                <div className="bg-pink-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Tóm tắt</h4>
                  <p className="text-gray-700">{selectedRecord.summary}</p>
                </div>
              )}

              {selectedRecord.details && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Chi tiết</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedRecord.details}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />

      {/* Register Popup */}
      {!isLoggedIn && (
        <RegisterPopup isOpen={showRegisterPopup} onClose={() => setShowRegisterPopup(false)} />
      )}
    </div>
  );
};

export default MedicalRecords;